import React, { useMemo } from 'react';
import { VStack, HStack, Text, IconName, Icon, Flex, Button, Box, Card } from '@televet/kibble-ui';
import { format, isThisYear, isToday } from 'date-fns';
import {
  PhoneCallParticipantEvent,
  PhoneCallParticipantEventEntity,
} from 'shared/providers/PhoneCallProvider/interfaces/PhoneCallParticipantEvent';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { useAppDispatch } from 'state/hooks';
import { openAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';

const eventEntityToIconName: Record<PhoneCallParticipantEventEntity, IconName> = {
  [PhoneCallParticipantEventEntity.Appointment]: 'calendarCheck',
  [PhoneCallParticipantEventEntity.ServiceReminder]: 'notification',
};

interface CallParticipantEventProps {
  event: PhoneCallParticipantEvent;
  isHistory?: boolean;
}

const PhoneCallParticipantEventItem = ({ event }: CallParticipantEventProps): JSX.Element => {
  const { eventEntity, eventEntityId, eventName, timestamp, petName } = event;

  const dispatch = useAppDispatch();

  const onItemClick = (): void => {
    if (eventEntity === PhoneCallParticipantEventEntity.Appointment) {
      dispatch(openAppointmentModal({ appointmentId: eventEntityId }));
    }
  };

  const { viewClinicPet } = useSidePanelSearch();
  const handlePetClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.stopPropagation();
    viewClinicPet({ clinicPetId: event.petId, forceOpen: true });
  };

  const timestampDisplay = useMemo(() => {
    if (!timestamp) {
      return null;
    }

    const dateFormat = isThisYear(timestamp) ? `MM/dd` : 'MM/dd/yy';

    switch (event.eventEntity) {
      case PhoneCallParticipantEventEntity.ServiceReminder:
        const date = format(timestamp, dateFormat);
        return (
          <>
            {event.isPastDue ? (
              <Text variant="danger" size="sm">
                Overdue {date}
              </Text>
            ) : (
              <Text variant="default" size="sm">
                Due {date}
              </Text>
            )}
          </>
        );
      case PhoneCallParticipantEventEntity.Appointment:
        return (
          <Text variant="default" size="sm">
            {format(timestamp, `${isToday(timestamp) ? "'Today at '" : `${dateFormat}' at '`}h:mm aa`)}
          </Text>
        );
    }
  }, [event, timestamp]);

  return (
    <Card
      display="flex"
      justifyContent="space-between"
      py={2}
      px={3}
      isInteractive={eventEntity === PhoneCallParticipantEventEntity.Appointment}
      onClick={onItemClick}
    >
      <HStack>
        <Icon name={eventEntityToIconName[eventEntity]} size="lg" />
        <VStack align="stretch" spacing={0}>
          <Text size="sm" lineHeight={1.3} fontWeight="semibold">
            {eventName}
          </Text>
          <Box lineHeight={1.3}>{timestampDisplay}</Box>
        </VStack>
      </HStack>
      <Flex align="center">
        <Button variant="ghostNeutral" size="sm" p={1} onClick={handlePetClick}>
          <Text fontWeight="normal" size="sm">
            {petName}
          </Text>
        </Button>
      </Flex>
    </Card>
  );
};

export default PhoneCallParticipantEventItem;
